import {
  ConnectedPosition,
  ConnectionPositionPair,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  OnDestroy,
  TemplateRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { TooltipWidgetComponent } from './tooltip-widget.component';

const NATIVELY_FOCUSABLE_TAGS = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
const HIDE_DELAY_MS = 400;
const FAIL_SAFE_HIDE_MS = 10000;

let nextTooltipId = 0;

@Directive({
  selector: '[appTooltipWidget]',
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'scheduleHide()',
    '(focusin)': 'onMouseEnter()',
    '(focusout)': 'hide()',
    '(window:scroll)': 'hide()',
    '(document:click)': 'onDocumentClick($event)',
    '(document:touchstart)': 'onDocumentTouch($event)',
    '[attr.tabindex]': 'hostTabIndex',
    '[attr.aria-describedby]': 'describedBy',
  },
})
export class TooltipWidgetDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private overlayPositionBuilder = inject(OverlayPositionBuilder);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);

  readonly showToolTip = input(true);
  readonly text = input<string>();
  readonly contentTemplate = input<TemplateRef<unknown>>();
  readonly templateData = input<object | null>(null);
  readonly panelClass = input<string>();
  readonly positionSet = input<ConnectedPosition[]>();
  /** Milliseconds to wait before opening, so passing the cursor over a trigger does not flash it. */
  readonly showDelay = input(0);

  /**
   * Whether a host that cannot otherwise take focus should be given a tab stop. Turn it off where
   * the tooltip repeats over many elements and the tab stops would outweigh the benefit; give those
   * hosts an accessible name instead.
   */
  readonly focusable = input(true);

  private readonly tooltipId = `app-tooltip-widget-${nextTooltipId++}`;

  private readonly hostIsNativelyFocusable = this.isHostNativelyFocusable();

  private overlayRef?: OverlayRef;
  private tooltipRef: ComponentRef<TooltipWidgetComponent> | null = null;
  private hideTimeout?: ReturnType<typeof setTimeout>;
  private failSafeTimeout?: ReturnType<typeof setTimeout>;
  private showTimeout?: ReturnType<typeof setTimeout>;

  protected get hostTabIndex(): number | null {
    if (!this.focusable() || !this.showToolTip() || this.hostIsNativelyFocusable) {
      return null;
    }

    return this.isHostAriaHidden() ? null : 0;
  }

  protected get describedBy(): string | null {
    return this.tooltipRef ? this.tooltipId : null;
  }

  onMouseEnter(): void {
    this.clearTimers();

    if (!this.showDelay()) {
      this.show();

      return;
    }

    this.showTimeout = setTimeout(() => this.show(), this.showDelay());
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isEventInsideTooltip(event)) {
      this.closeToolTip();
    }
  }

  /** A tap has no pointer to move away, so a tap on the host opens and one elsewhere dismisses. */
  onDocumentTouch(event: TouchEvent): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) {
      this.onMouseEnter();

      return;
    }

    if (!this.isEventInsideTooltip(event)) {
      this.closeToolTip();
    }
  }

  hide(): void {
    this.closeToolTip();
  }

  ngOnDestroy(): void {
    this.closeToolTip();
    this.overlayRef?.dispose();
  }

  private isEventInsideTooltip(event: Event): boolean {
    const target = event.target as Node;

    return (
      this.elementRef.nativeElement.contains(target) ||
      (!!this.overlayRef?.hasAttached() && this.overlayRef.overlayElement.contains(target))
    );
  }

  /**
   * A tab stop inside an `aria-hidden` subtree focuses something no screen reader can announce, which
   * axe reports as `aria-hidden-focus`. `mat-icon` hides itself unless the template passes its own
   * `aria-hidden`, so icon hosts land here; read live rather than cached, since an ancestor can flip.
   */
  private isHostAriaHidden(): boolean {
    return this.elementRef.nativeElement.closest('[aria-hidden="true"]') !== null;
  }

  private isHostNativelyFocusable(): boolean {
    const host = this.elementRef.nativeElement;

    return NATIVELY_FOCUSABLE_TAGS.includes(host.tagName) || host.hasAttribute('tabindex');
  }

  private show(): void {
    this.ensureOverlay();

    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      this.tooltipRef = this.overlayRef.attach(new ComponentPortal(TooltipWidgetComponent));
      this.tooltipRef.instance.text = this.text();
      this.tooltipRef.instance.contentTemplate = this.contentTemplate();
      this.tooltipRef.instance.templateData = this.templateData();

      const tooltipElement = this.overlayRef.overlayElement;

      tooltipElement.id = this.tooltipId;
      tooltipElement.setAttribute('role', 'tooltip');

      setTimeout(() => {
        tooltipElement.addEventListener('mouseenter', this.clearTimers);
        tooltipElement.addEventListener('mouseleave', this.scheduleHide);
      });

      this.failSafeTimeout = setTimeout(() => {
        this.closeToolTip();
      }, FAIL_SAFE_HIDE_MS);
    }
  }

  protected scheduleHide = () => {
    this.clearTimers();
    this.hideTimeout = setTimeout(() => {
      this.closeToolTip();
    }, HIDE_DELAY_MS);
  };

  private clearTimers = () => {
    clearTimeout(this.hideTimeout);
    clearTimeout(this.failSafeTimeout);
    clearTimeout(this.showTimeout);
  };

  private closeToolTip(): void {
    this.clearTimers();

    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }

    this.tooltipRef = null;
  }

  private ensureOverlay(): void {
    if (!this.showToolTip() || this.overlayRef) {
      return;
    }

    const defaultPositions = [
      new ConnectionPositionPair(
        { originX: 'center', originY: 'bottom' },
        { overlayX: 'center', overlayY: 'top' }
      ),
      new ConnectionPositionPair(
        { originX: 'center', originY: 'top' },
        { overlayX: 'center', overlayY: 'bottom' }
      ),
    ];

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.positionSet() ?? defaultPositions);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      panelClass: this.panelClass(),
    });

    positionStrategy.positionChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(pos => {
      if (this.tooltipRef) {
        this.tooltipRef.instance.showAboveTheParent = pos.connectionPair.originY === 'top';
      }
    });

    fromEvent(window, 'scroll', { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeToolTip());
  }
}
